import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FAQ_CATEGORIES } from '@/data/content';
import { Plus, Minus } from 'lucide-react';

function FAQItem({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border-b border-white/10">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left py-5 flex items-center justify-between gap-4 group"
      >
        <span className="font-display text-base md:text-lg text-slate-100 group-hover:text-cyan-300 transition-colors">
          {q}
        </span>
        <span className="flex-shrink-0 w-8 h-8 rounded-full border border-white/10 flex items-center justify-center group-hover:border-cyan-400/40">
          {open ? <Minus className="w-3.5 h-3.5 text-cyan-300" /> : <Plus className="w-3.5 h-3.5 text-slate-300" />}
        </span>
      </button>
      <div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100 pb-5' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <p className="text-sm text-slate-400 leading-relaxed pr-12">{a}</p>
        </div>
      </div>
    </div>
  );
}

export default function FaqsPage() {
  const location = useLocation();
  const [active, setActive] = useState('general');

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      if (FAQ_CATEGORIES.some((c) => c.id === id)) setActive(id);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  const activeCat = FAQ_CATEGORIES.find((c) => c.id === active) || FAQ_CATEGORIES[0];

  return (
    <div>
      <section className="relative py-20 md:py-28 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 viso-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-[0.35em] text-cyan-300 mb-4">Resources · FAQs</div>
          <h1 className="font-display text-4xl md:text-6xl font-bold max-w-4xl leading-[1.05]">
            <span className="text-gradient">Answers to the questions we hear most.</span>
          </h1>
          <p className="text-slate-400 mt-5 max-w-2xl">
            What the platform is, how it reads container damage automatically, which sites it fits, and how deployment is scoped against existing CCTV.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-10">
          {/* Categories rail */}
          <aside className="md:col-span-3">
            <div className="sticky top-24">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Categories</div>
              <ul className="space-y-1">
                {FAQ_CATEGORIES.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => setActive(c.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${active === c.id ? 'bg-cyan-400/10 text-cyan-300 border border-cyan-400/30' : 'text-slate-300 hover:text-white hover:bg-white/[0.03] border border-transparent'}`}
                    >
                      {c.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Content */}
          <div className="md:col-span-9">
            <div id={activeCat.id}>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-cyan-300 mb-3">
                {activeCat.name} questions
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-8">{activeCat.heading}</h2>
              <div>
                {activeCat.items.map((it, i) => (
                  <FAQItem key={i} q={it.q} a={it.a} defaultOpen={i === 0} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
